import React from 'react';
import styled from 'styled-components';
import Avatar from './Avatar';
import { useAppSelector } from '../../store/hooks';
import { avatarApi } from '../../services/avatar';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  width: 100%;
`;

const AvatarContainer = styled.div`
  position: relative;
  display: inline-block;
  
  &:hover .overlay {
    opacity: 1;
  }
`;

const CameraOverlay = styled.label`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 600;
  gap: 4px;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  background: ${p => {
    if (p.$variant === 'danger') return 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)';
    if (p.$variant === 'secondary') return '#f3f4f6';
    return 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)';
  }};
  color: ${p => (p.$variant === 'secondary' ? '#374151' : '#fff')};
  border: ${p => (p.$variant === 'secondary' ? '1px solid #e5e7eb' : 'none')};
  border-radius: 12px;
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  cursor: pointer;
  box-shadow: ${p => (p.$variant === 'secondary' ? 'none' : '0 4px 12px rgba(99, 102, 241, 0.25)')};
  transition: all 0.2s ease;
  font-size: 0.875rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${p => (p.$variant === 'secondary' ? '0 2px 4px rgba(0,0,0,0.05)' : '0 6px 16px rgba(99, 102, 241, 0.35)')};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const InfoBox = styled.div`
  text-align: center;
`;

const Hint = styled.div`
  color: #6b7280;
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-weight: 600;
`;

const SuccessText = styled.div`
  color: #16a34a;
  font-weight: 600;
`;

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

const AvatarUploader: React.FC = () => {
  const { user } = useAppSelector((s) => s.auth);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);
  const [err, setErr] = React.useState<string | null>(null);

  if (!user) return null;

  const initials = `${(user.firstName || '').trim().charAt(0)}${(user.lastName || '').trim().charAt(0)}` || (user.username || user.email || 'U').charAt(0);
  const legacyUrl = user.profilePicture || user.profileImage || null;

  const handleUpload = async (f: File) => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await avatarApi.upload(f);
      setMsg('Avatar updated');
      window.dispatchEvent(new Event('avatar-updated'));
    } catch (e: any) {
      setErr(e?.message || 'Failed to upload avatar');
    } finally {
      setBusy(false);
    }
  };

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] || null;
    setErr(null);
    setMsg(null);
    if (!f) return;
    if (!['image/jpeg','image/png','image/webp'].includes(f.type)) {
      setErr('Supported image types: JPG, PNG, WebP');
      return;
    }
    if (f.size > MAX_SIZE) {
      setErr('Please choose an image up to 5 MB');
      return;
    }
    handleUpload(f);
  };

  const doRemove = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await avatarApi.remove();
      setMsg('Avatar removed');
      window.dispatchEvent(new Event('avatar-updated'));
    } catch (e: any) {
      setErr(e?.message || 'Failed to remove avatar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Wrapper>
      <AvatarContainer>
        <Avatar size={120} rounded src={avatarApi.url(Date.now())} fallbackSrc={legacyUrl} initials={initials} />
        <CameraOverlay htmlFor="avatar-upload" className="overlay">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          <span>Change</span>
        </CameraOverlay>
      </AvatarContainer>
      
      <Controls>
        <HiddenInput id="avatar-upload" type="file" accept="image/jpeg,image/png,image/webp" onChange={onFileChange} disabled={busy} />
        
        {legacyUrl && (
          <ActionButtons>
            <Button $variant="danger" onClick={doRemove} disabled={busy}>Remove</Button>
          </ActionButtons>
        )}
        
        <InfoBox>
          {msg && <SuccessText role="status">{msg}</SuccessText>}
          {err && <ErrorText role="alert">{err}</ErrorText>}
          {busy && <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Uploading...</div>}
        </InfoBox>
      </Controls>
    </Wrapper>
  );
};

export default AvatarUploader;
